import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save, Send, ExternalLink, Mail, Shield, RotateCcw, RefreshCw, Settings, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === Per-flow step config — mirrors backend FLOWS dict ====================
// Each tab renders these rows; each row = step (status key) + dropdown to
// pick a template + per-step "Send test" button.
const FLOWS = {
  garantie: {
    title: "Garanții",
    icon: Shield,
    color: "emerald",
    steps: [
      { status: "pending",         label: "Creată (Așteaptă AWB)",     desc: "Stare inițială — fără AWB încă. De regulă fără email." },
      { status: "pickup_issued",   label: "Preluare Client",            desc: "AWB de ridicare emis. Mail 1 către client." },
      { status: "in_transit",      label: "În drum spre service",       desc: "Curierul a scanat coletul (Sameday: Ridicat)." },
      { status: "at_service",      label: "Verificare Service",         desc: "Coletul a ajuns la noi (Sameday: Livrat pe AWB ridicare). Mail 2." },
      { status: "delivery_issued", label: "În livrare către client",    desc: "AWB de livrare emis. Mail 3." },
      { status: "completed",       label: "Finalizată",                 desc: "Coletul livrat clientului (Sameday: Livrat pe AWB livrare). Mail 4." },
    ],
  },
  retur: {
    title: "Retururi",
    icon: RotateCcw,
    color: "sky",
    steps: [
      { status: "pending",                     label: "Cerere Creată",                  desc: "Stare inițială — fără AWB încă." },
      { status: "pickup_issued",               label: "Preluare Client",                desc: "AWB de ridicare emis." },
      { status: "in_transit_warehouse",        label: "Către Depozit",                  desc: "Curierul a scanat coletul (Sameday: Ridicat)." },
      { status: "at_warehouse_pending_refund", label: "În verificare, Bani de returnat",desc: "Coletul a ajuns la depozit (Sameday: Livrat)." },
      { status: "completed",                   label: "Finalizată (Bani Returnați)",    desc: 'MANUAL — admin apasă „Bani Returnați" în pagina cererii.' },
    ],
  },
  schimb: {
    title: "Schimb",
    icon: RefreshCw,
    color: "violet",
    steps: [
      { status: "pending",              label: "În așteptare",                desc: "Stare inițială — cerere creată, fără AWB emis încă." },
      { status: "outbound_in_delivery", label: "În livrare către client",    desc: "AWB-urile Sameday (Livrare + Ridicare) au fost emise." },
      { status: "return_in_transit",    label: "În tranzit către depozit",   desc: "AWB Livrare = LIVRAT → curierul a lăsat coletul nou și ridică coletul vechi." },
      { status: "completed",            label: "Finalizată",                 desc: "AWB Ridicare = LIVRAT la depozit." },
    ],
  },
  livrare: {
    title: "Livrare",
    icon: Truck,
    color: "sky",
    steps: [
      { status: "pending",     label: "Cerere Livrare Creată", desc: "Stare inițială — cerere de livrare creată, fără AWB emis încă." },
      { status: "in_delivery", label: "În drum spre client",   desc: "AWB Sameday emis (opțional cu ramburs)." },
      { status: "completed",   label: "Finalizată",            desc: "AWB = LIVRAT la client." },
    ],
  },
};

const tplKey = (flow, status) => `tpl_${flow}_${status}`;

function TabButton({ flow, def, active, onClick }) {
  const Icon = def.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`warranty-tab-${flow}`}
      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 ${
        active ? "border-emerald-400 text-emerald-200 bg-emerald-500/5" : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/40"
      }`}
    >
      <Icon className="h-4 w-4" /> {def.title}
    </button>
  );
}

function StepRow({ flow, step, cfg, templates, update, sendTest, sendingKey, overrideEmail, setOverrideEmail }) {
  const k = tplKey(flow, step.status);
  const isTesting = sendingKey === `${flow}.${step.status}`;
  const hasTpl = !!cfg[k];

  return (
    <div data-testid={`warranty-step-${flow}-${step.status}`}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-100">{step.label}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{step.desc}</div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-mono shrink-0">{step.status}</div>
      </div>
      <select data-testid={`warranty-${k}-select`}
        value={cfg[k] || ""}
        onChange={(e) => update(k, e.target.value || null)}
        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none">
        <option value="">— Nu trimite email pentru acest pas —</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <input
          data-testid={`warranty-${k}-test-email`}
          type="email"
          value={overrideEmail}
          onChange={(e) => setOverrideEmail(e.target.value)}
          placeholder="email destinatar pentru test (gol = admin)"
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 outline-none"
        />
        <button
          data-testid={`warranty-${k}-test-send`}
          disabled={!hasTpl || isTesting}
          onClick={() => sendTest(flow, step.status)}
          title={!hasTpl ? "Selectează un șablon mai întâi" : "Trimite email de test cu acest șablon"}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-100 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="h-3.5 w-3.5" /> {isTesting ? "Se trimite..." : "Test"}
        </button>
      </div>
    </div>
  );
}

export default function WarrantyEmailSettingsModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingKey, setSendingKey] = useState(null);
  const [activeTab, setActiveTab] = useState("garantie");
  const [overrideEmail, setOverrideEmail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/returns/warranty/email-config`),
          axios.get(`${API}/email-templates`),
        ]);
        setCfg(a.data);
        setTemplates(b.data.items || b.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/returns/warranty/email-config`, cfg);
      setCfg(r.data);
      toast.success("Setări salvate.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (flow, status) => {
    const key = `${flow}.${status}`;
    setSendingKey(key);
    try {
      const r = await axios.post(`${API}/returns/warranty/test-step-email`, {
        flow, status,
        to: overrideEmail || undefined,
      });
      if (r.data?.sent) {
        toast.success(`Email test trimis către ${r.data.to} (${r.data.template_name || ""})`);
      } else {
        toast.error("Nu s-a trimis nimic.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSendingKey(null);
    }
  };

  const flowDef = FLOWS[activeTab];
  // Resolve the live public base URL: prefer the user-set `tracking_url_base`
  // (the "/track" suffix is stripped to get the bare domain) and fall back to
  // the deployed domain (`REACT_APP_BACKEND_URL`). This is the value used to
  // build the public Retur / Garanție form links displayed below.
  const publicBase = useMemo(() => {
    const fromCfg = (cfg?.tracking_url_base || "").replace(/\/track\/?$/, "").replace(/\/$/, "");
    if (fromCfg) return fromCfg;
    return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  }, [cfg?.tracking_url_base]);
  const publicFormUrl    = useMemo(() => `${publicBase}/return-form`,   [publicBase]);
  const publicWarrantyUrl = useMemo(() => `${publicBase}/warranty-form`, [publicBase]);

  if (!cfg) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="rounded-xl bg-zinc-900 p-6 text-zinc-300">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div data-testid="warranty-email-modal" className="w-full max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-zinc-800 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-zinc-50 flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-400" /> Setări Email — Returnări & Garanții
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Configurează email-ul automat pentru fiecare pas, separat pentru Garanție / Retur / Schimb / Livrare.
            </p>
          </div>
          <button onClick={onClose} data-testid="warranty-email-close"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Master toggle */}
        <div className="border-b border-zinc-800 px-6 py-3 bg-zinc-900/40">
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="warranty-email-enabled" type="checkbox"
              checked={cfg.enabled !== false}
              onChange={(e) => update("enabled", e.target.checked)}
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-800 text-emerald-500" />
            <span className="text-sm font-medium text-zinc-200">Trimite automat emailuri (master switch)</span>
            <span className="text-[11px] text-zinc-500">— dacă e dezactivat, niciun pas nu trimite email.</span>
          </label>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 bg-zinc-950">
          {Object.entries(FLOWS).map(([f, def]) => (
            <TabButton key={f} flow={f} def={def} active={activeTab === f} onClick={() => setActiveTab(f)} />
          ))}
          <button
            onClick={() => setActiveTab("general")}
            data-testid="warranty-tab-general"
            className={`px-4 py-2.5 text-sm font-medium transition border-b-2 ${
              activeTab === "general"
                ? "border-emerald-400 text-emerald-200 bg-emerald-500/5"
                : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/40"
            }`}
          >
            <Settings className="inline h-4 w-4 mr-1.5" /> General
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4 space-y-3 flex-1">
          {activeTab !== "general" && flowDef && (
            <>
              <div className="text-xs text-zinc-500 mb-2">
                Selectează șablon pentru fiecare pas. Lasă gol pentru a NU trimite email pe acel pas. Butonul &bdquo;Test&rdquo; trimite imediat un email cu date demo.
              </div>
              {flowDef.steps.map((step) => (
                <StepRow key={step.status} flow={activeTab} step={step}
                  cfg={cfg} templates={templates} update={update}
                  sendTest={sendTest} sendingKey={sendingKey}
                  overrideEmail={overrideEmail} setOverrideEmail={setOverrideEmail} />
              ))}
              {/* Admin-notify slot per flow (fires on creation) */}
              <div className="rounded-xl border border-amber-700/30 bg-amber-500/5 p-4 mt-3 space-y-2">
                <div className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  Notificare administrator (la creare {flowDef.title.toLowerCase()})
                </div>
                <select
                  data-testid={`warranty-tpl_${activeTab}_admin_notify-select`}
                  value={cfg[`tpl_${activeTab}_admin_notify`] || ""}
                  onChange={(e) => update(`tpl_${activeTab}_admin_notify`, e.target.value || null)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none"
                >
                  <option value="">— Nu trimite notificare admin —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <div className="text-[11px] text-zinc-500">
                  Trimis pe <code className="text-emerald-300">{cfg.admin_notify_email || "—"}</code> când se creează o cerere de tip {flowDef.title.toLowerCase()}.
                </div>
              </div>
            </>
          )}

          {activeTab === "general" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400 mb-1.5">
                  Link de tracking (baza URL)
                </label>
                <input data-testid="warranty-tracking-url"
                  value={cfg.tracking_url_base || ""}
                  onChange={(e) => update("tracking_url_base", e.target.value)}
                  placeholder={`${(process.env.REACT_APP_BACKEND_URL || "https://bragus-app.ro").replace(/\/$/, "")}/track`}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
                <p className="text-[11px] text-zinc-500 mt-1">
                  Folosește <code className="text-emerald-300">${'{request.tracking_url}'}</code> în template-uri.
                  {!cfg.tracking_url_base && (
                    <span className="ml-1">Lăsat gol → se folosește automat <span className="text-emerald-300 font-mono">{publicBase}/track</span>.</span>
                  )}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400 mb-1.5">
                  Email administrator (pentru notificări + test send default)
                </label>
                <input data-testid="warranty-admin-email" type="email"
                  value={cfg.admin_notify_email || ""}
                  onChange={(e) => update("admin_notify_email", e.target.value)}
                  placeholder="contact@bragus.ro"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400 mb-1.5">
                  Polling Sameday — frecvență sincronizare AWB-uri (minute)
                </label>
                <select
                  data-testid="warranty-poll-minutes"
                  value={cfg.sameday_poll_minutes || 15}
                  onChange={(e) => update("sameday_poll_minutes", parseInt(e.target.value, 10))}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none"
                >
                  <option value={15}>La 15 minute (default, responsive)</option>
                  <option value={30}>La 30 minute</option>
                  <option value={60}>La 1 oră (conservator)</option>
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Cât de des verifică sistemul status-ul AWB-urilor active la Sameday pentru a auto-trece la pasul următor.
                </p>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 space-y-3">
                <div className="text-sm font-bold text-emerald-300">Linkuri publice (clienți)</div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400 mb-1.5">
                    Link Politică de Confidențialitate (folosit în AMBELE formulare)
                  </label>
                  <input data-testid="warranty-privacy-url" type="url"
                    value={cfg.privacy_policy_url || ""}
                    onChange={(e) => update("privacy_policy_url", e.target.value)}
                    placeholder="https://bragus-app.ro/politica-confidentialitate"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400 mb-1.5">
                    Link Politică de Retur (apare doar în formularul de Retur)
                  </label>
                  <input data-testid="warranty-return-url" type="url"
                    value={cfg.return_policy_url || ""}
                    onChange={(e) => update("return_policy_url", e.target.value)}
                    placeholder="https://bragus-app.ro/politica-retur"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
                </div>
                <div className="space-y-2 pt-2 border-t border-zinc-800/60">
                  <div className="text-[11px] text-zinc-400 leading-relaxed">
                    <span className="font-medium text-zinc-300">📋 Formular RETUR (trimite clienților):</span>
                    <div className="mt-0.5">
                      <a data-testid="public-form-link" href={publicFormUrl} target="_blank" rel="noreferrer"
                        className="text-emerald-300 hover:text-emerald-200 underline break-all font-mono">
                        {publicFormUrl}
                      </a>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-400 leading-relaxed">
                    <span className="font-medium text-zinc-300">🛡️ Formular GARANȚIE (trimite clienților):</span>
                    <div className="mt-0.5">
                      <a data-testid="public-warranty-form-link" href={publicWarrantyUrl} target="_blank" rel="noreferrer"
                        className="text-violet-300 hover:text-violet-200 underline break-all font-mono">
                        {publicWarrantyUrl}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 text-xs text-zinc-400 space-y-1">
                <div className="font-medium text-zinc-300 mb-1">Variabile disponibile în template-uri:</div>
                <code className="block text-emerald-300">${'{request.number}'} ${'{request.status_label}'} ${'{request.tracking_url}'}</code>
                <code className="block text-emerald-300">${'{customer.name}'} ${'{customer.phone}'} ${'{customer.email}'}</code>
                <code className="block text-emerald-300">${'{product.name}'} ${'{product.part_number}'} ${'{product.image}'}</code>
                <code className="block text-emerald-300">${'{awb_pickup.number}'} ${'{awb_delivery.number}'}</code>
                <code className="block text-emerald-300">${'{order.id}'}</code>
                <div className="pt-2">
                  <a href="/email-templates" target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300">
                    <ExternalLink className="h-3 w-3" /> Editează template-urile
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-6 py-3">
          <button onClick={onClose}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-600">
            Anulează
          </button>
          <button data-testid="warranty-email-save" disabled={saving} onClick={save}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/20 px-3 py-1.5 text-sm font-medium text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
}
